import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { TabsContent } from '@/components/ui/tabs';
import { 
  File, 
  Landmark, 
  Calculator, 
  FolderOpen, 
  ChevronUp, 
  ChevronDown, 
  ExternalLink, 
  Edit, 
  Trash2, 
  Plus,
  Lock
} from 'lucide-react';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
  const [year, month, day] = datePart.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const DocumentSection = ({ 
  type, 
  title, 
  icon: Icon, 
  colorClass, 
  documents, 
  expanded, 
  onToggle, 
  canEdit, 
  onView, 
  onEdit, 
  onDelete 
}) => {
  const filteredDocs = documents.filter(d => d.document_type === type);
  
  return (
    <div className="mb-4">
      <button 
        onClick={onToggle}
        className={`w-full flex items-center justify-between p-3 ${colorClass.bg} hover:${colorClass.bgHover} rounded-lg transition-colors`}
      >
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${colorClass.icon}`} />
          <span className="text-sm font-semibold text-slate-700">{title}</span>
          <span className={`text-xs text-slate-500 ${colorClass.badge} px-2 py-0.5 rounded-full`}>
            {filteredDocs.length}
          </span>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-slate-500" />
        ) : (
          <ChevronDown className="w-4 h-4 text-slate-500" />
        )}
      </button>
      {expanded && (
        <div className="mt-2 pl-2">
          {filteredDocs.length === 0 ? (
            <p className="text-sm text-slate-400 italic py-2">No {title.toLowerCase()} uploaded</p>
          ) : (
            <div className="space-y-2">
              {filteredDocs.map(doc => (
                <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <File className={`w-5 h-5 ${colorClass.icon}`} />
                      {doc.is_password_protected && (
                        <Lock className="w-3 h-3 text-amber-600 absolute -bottom-1 -right-1 bg-white rounded-full" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm">{doc.name}</p>
                        {doc.is_password_protected && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded flex items-center gap-1">
                            <Lock className="w-3 h-3" />
                            Protected
                          </span>
                        )}
                        {!doc.cloudinary_url && (
                          <span className="text-xs bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded" data-testid="no-file-badge">
                            No file attached
                          </span>
                        )}
                      </div>
                      {doc.description && <p className="text-xs text-slate-500">{doc.description}</p>}
                      <p className="text-xs text-slate-400 mt-1">
                        Added {formatDate(doc.uploaded_at)} {doc.uploaded_by && `by ${doc.uploaded_by}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {doc.cloudinary_url && (
                      <Button variant="ghost" size="sm" className={colorClass.icon} title="View/Download" onClick={() => onView(doc)}>
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    )}
                    {canEdit && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => onEdit(doc)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => onDelete(doc.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const DocumentsTab = ({ 
  documents, 
  canEditLLC, 
  onAddDocument, 
  onEditDocument, 
  onDeleteDocument, 
  onViewDocument 
}) => {
  const [expandedSections, setExpandedSections] = useState(() => {
    const saved = localStorage.getItem('docSectionsExpanded');
    return saved ? JSON.parse(saved) : {
      bank_statement: true,
      tax_return: true,
      other: true
    };
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => {
      const newState = {
        ...prev,
        [section]: !prev[section]
      };
      localStorage.setItem('docSectionsExpanded', JSON.stringify(newState));
      return newState;
    });
  };

  const handleEdit = (doc) => {
    onEditDocument(doc);
  };

  return (
    <TabsContent value="documents" className="mt-4">
      <Card className="border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="font-serif">Document Manager</CardTitle>
            <CardDescription>Bank statements, tax returns, and other documents</CardDescription>
          </div>
          {canEditLLC && (
            <Button onClick={onAddDocument} className="bg-slate-900 hover:bg-slate-800">
              <Plus className="w-4 h-4 mr-2" />
              Add Document
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <DocumentSection
            type="bank_statement"
            title="Bank Statements"
            icon={Landmark}
            colorClass={{
              bg: 'bg-blue-50',
              bgHover: 'bg-blue-100',
              icon: 'text-blue-600',
              badge: 'bg-blue-200'
            }}
            documents={documents}
            expanded={expandedSections.bank_statement}
            onToggle={() => toggleSection('bank_statement')}
            canEdit={canEditLLC}
            onView={onViewDocument}
            onEdit={handleEdit}
            onDelete={onDeleteDocument}
          />

          <DocumentSection
            type="tax_return"
            title="Tax Returns"
            icon={Calculator}
            colorClass={{
              bg: 'bg-green-50',
              bgHover: 'bg-green-100',
              icon: 'text-green-600',
              badge: 'bg-green-200'
            }}
            documents={documents}
            expanded={expandedSections.tax_return}
            onToggle={() => toggleSection('tax_return')}
            canEdit={canEditLLC}
            onView={onViewDocument}
            onEdit={handleEdit}
            onDelete={onDeleteDocument}
          />

          <DocumentSection
            type="other"
            title="Other Documents"
            icon={FolderOpen}
            colorClass={{
              bg: 'bg-slate-100',
              bgHover: 'bg-slate-200',
              icon: 'text-slate-600',
              badge: 'bg-slate-300'
            }}
            documents={documents}
            expanded={expandedSections.other}
            onToggle={() => toggleSection('other')}
            canEdit={canEditLLC}
            onView={onViewDocument}
            onEdit={handleEdit}
            onDelete={onDeleteDocument}
          />
        </CardContent>
      </Card>
    </TabsContent>
  );
};
